import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSignalRContext } from '../components/Contexts/SignalRContext'
import { offlineDb, type MutationQueueItem } from './db'
import { syncAjax } from '../ajax/SyncAjax'
import { SyncOperation, SyncStatus } from '../DTOs/Sync/SyncDtos'
import type { SyncRequest } from '../DTOs/Sync/SyncDtos'

function operationCode(op: MutationQueueItem['operation']) {
  if (op === 'create') return SyncOperation.Create
  if (op === 'update') return SyncOperation.Update
  return SyncOperation.Delete
}

function buildSyncRequest(items: MutationQueueItem[]): SyncRequest {
  const request: SyncRequest = { stoppages: [], loadings: [], diggingPatterns: [], patternBlasts: [], completeShifts: [], geoRehandles: [] }
  for (const item of items) {
    const base = {
      clientId: item.clientId,
      operation: operationCode(item.operation),
      shiftId: item.shiftId,
      rowVersion: item.rowVersion,
      data: item.data,
    }
    if (item.entity === 'stoppage') {
      const { plant: _p, stoppageType: _st, stoppageSubType: _sst, location: _l, ...safeData } = (base.data ?? {}) as any
      request.stoppages.push({ ...base, data: safeData, stoppageId: item.entityId })
    } else if (item.entity === 'loading') {
      request.loadings.push({ ...base, loadingId: item.entityId })
    } else if (item.entity === 'diggingPattern') {
      request.diggingPatterns.push({ ...base, diggingPatternId: item.entityId })
    } else if (item.entity === 'patternBlast') {
      request.patternBlasts.push({ ...base })
    } else if (item.entity === 'completeShift') {
      request.completeShifts.push({ clientId: item.clientId, shiftId: item.shiftId })
    } else if (item.entity === 'geoRehandle') {
      request.geoRehandles.push({ clientId: item.clientId, operation: operationCode(item.operation), geoRehandleId: item.entityId, rowVersion: item.rowVersion, data: item.data })
    }
  }
  return request
}

export function useSyncWorker() {
  const queryClient = useQueryClient()
  const { connection } = useSignalRContext()
  const pendingCount = useLiveQuery(() => offlineDb.mutationQueue.count(), []) ?? 0
  const isSyncingRef = useRef(false)

  const runSync = useCallback(async () => {
    if (isSyncingRef.current) return
    isSyncingRef.current = true
    try {
      // Note: read the queue fresh from Dexie rather than from the useLiveQuery-backed
      // itemsRef, which lags a tick behind writes. A stale snapshot here re-sends items
      // that a previous run already synced-and-deleted, creating duplicate server rows.
      const snapshot = await offlineDb.mutationQueue.orderBy('createdAt').toArray()
      if (snapshot.length === 0) return
      const request = buildSyncRequest(snapshot)
      const response = await syncAjax.sync(request)
      if (!response.success || !response.data) return

      const byClientId = new Map(snapshot.map(i => [i.clientId, i]))
      const staleEntities = new Set<MutationQueueItem['entity']>()

      await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
        for (const result of response.data!.results) {
          const item = byClientId.get(result.clientId)
          if (!item) continue
          staleEntities.add(item.entity)
          if (result.status === SyncStatus.Applied) {
            await offlineDb.mutationQueue.where('clientId').equals(result.clientId).delete()
          } else {
            await offlineDb.syncIssues.add({
              clientId: result.clientId,
              entity: item.entity,
              operation: item.operation,
              status: result.status,
              entityId: item.entityId,
              shiftId: item.shiftId,
              serverState: result.serverState,
              clientData: item.data,
              createdAt: Date.now(),
            })
            await offlineDb.mutationQueue.where('clientId').equals(result.clientId).delete()
          }
        }
      })

      // Note: invalidation is the reconciliation step. Refetching replaces any optimistic
      // temp-id rows with the real server rows, so every query key that an entity's optimistic
      // write can touch must be invalidated here or its temp rows linger until a manual refresh.
      for (const entity of staleEntities) {
        if (entity === 'stoppage') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'stoppages'] })
        } else if (entity === 'loading') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'truckLoads'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
        } else if (entity === 'diggingPattern') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
        } else if (entity === 'geoRehandle') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'rehandles'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'crushing'] })
        } else if (entity === 'completeShift') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
          await queryClient.invalidateQueries({ queryKey: ['operations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'production'] })
          await queryClient.invalidateQueries({ queryKey: ['production'] })
        } else {
          // Note: patternBlast and any future entity — refresh the blast options and the
          // location/production tables that reference blasts.
          await queryClient.invalidateQueries({ queryKey: ['references', 'blastmasterIds'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
        }
      }
    } finally {
      isSyncingRef.current = false
    }
  }, [queryClient])

  useEffect(() => {
    let active = true
    const trigger = () => { if (active && navigator.onLine) runSync() }

    trigger()
    window.addEventListener('online', trigger)
    connection?.onreconnected(() => { if (active) runSync() })

    return () => {
      active = false
      window.removeEventListener('online', trigger)
    }
    // Note: pendingCount is a dependency so a newly-enqueued mutation kicks off a sync
    // attempt immediately when online, instead of waiting for the next reconnect event.
  }, [connection, runSync, pendingCount])
}
